import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export const usePushNotifications = () => {
    const isSupported =
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(isSupported);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isSupported) return;

        navigator.serviceWorker.ready.then((reg) =>
            reg.pushManager.getSubscription()
        ).then((existing) => {
            setIsSubscribed(!!existing);
        }).catch(() => {
            // non-fatal: subscription state stays false
        }).finally(() => {
            setIsLoading(false);
        });
    }, [isSupported]);

    const subscribe = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                setError('permission_denied');
                return;
            }

            const { vapid_public_key: vapidPublicKey } = await api.getPushVapidKey();
            const reg = await navigator.serviceWorker.ready;
            const subscription = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });

            await api.storePushSubscription(subscription.toJSON());
            setIsSubscribed(true);
        } catch (err) {
            setError(err.message || 'subscribe_failed');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const unsubscribe = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const subscription = await reg.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
                await api.deletePushSubscription(subscription.endpoint);
            }
            setIsSubscribed(false);
        } catch (err) {
            setError(err.message || 'unsubscribe_failed');
        } finally {
            setIsLoading(false);
        }
    }, []);

    return { isSupported, isSubscribed, isLoading, error, subscribe, unsubscribe };
};
